import { Box, css, IconButton, Stack, styled, Typography } from '@mui/material';
import { CaretLeftIcon, XIcon } from '@phosphor-icons/react';
import { getRouteApi, useLocation } from '@tanstack/react-router';
import type { ReactNode} from 'react';
import { useMemo } from 'react';

import { IconButtonLink } from '@/components/router/IconButtonLink';
import { navItems } from '@/data/navItems';
import { transientOptions } from '@/utils/transient-options';

const Root = styled(
  Box,
  transientOptions,
)<{ $visible: boolean }>(
  ({ theme, $visible }) => css`
    display: flex;
    flex-direction: column;
    min-width: 0;
    width: ${$visible ? 'auto' : '0'};
    height: 100%;
    opacity: ${$visible ? 1 : 0};
    background-color: #f6f6f6cc;
    backdrop-filter: blur(5px);
    border-left: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
    padding: ${$visible ? `${theme.spacing(2)} ${theme.spacing(3)}` : '0'};
    transition:
      width 0.3s,
      opacity 0.3s,
      padding 0.3s;
    pointer-events: ${$visible ? 'auto' : 'none'};
    z-index: 10;
  `,
);

interface SidebarContentProps {
  children: ReactNode;
}

const routeApi = getRouteApi('/_map');

export function SidebarContent({ children }: SidebarContentProps) {
  const navigate = routeApi.useNavigate();
  const search = routeApi.useSearch();
  const { pathname } = useLocation();

  const showBackButton = typeof search.resultIndex === 'number' && pathname === '/route';

  const title = useMemo(() => {
    if (pathname === '/route' && typeof search.resultIndex === 'number') {
      return 'Routendetails';
    }
    const item = navItems.find((n) => n.to === pathname);

    return item?.label;
  }, [pathname, search.resultIndex]);

  const handleBack = () => {
    const { resultIndex, ...rest } = search as { resultIndex?: number } & Record<string, unknown>;
    navigate({ to: '/route', search: rest });
  };

  return (
    <Root $visible>
      <Stack direction="column" width="100%">
        <Stack alignItems="flex-end" justifyContent="center">
          {showBackButton ? (
            <IconButton aria-label="Zurück" onClick={handleBack} size="small">
              <CaretLeftIcon size={24} />
            </IconButton>
          ) : (
            <IconButtonLink aria-label="Schließen" to="/">
              <XIcon size={24} />
            </IconButtonLink>
          )}
        </Stack>
        {title ? <Stack alignItems="flex-start" justifyContent="center">
            <Typography variant="h4">{title}</Typography>
          </Stack> : null}
      </Stack>

      {children}
    </Root>
  );
}
